import React, { useEffect, useState } from "react";
import axios from "axios";
import { BookOpen, ChevronRight, ChevronLeft, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Worker-side viewer for their company's tutorials. */
const WorkerTutorialsPage = () => {
  const [tutorials, setTutorials] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/tutorials`)
      .then(r => { if (mounted) setTutorials(r.data || []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="space-y-4" data-testid="worker-tutorials-page">
      <div>
        <h1 className="text-3xl font-bold uppercase tracking-tight">Tutorials</h1>
        <p className="text-slate-500 text-sm mt-1">Guides for your team — published by your admin.</p>
      </div>
      {loading ? (
        <div className="text-center py-10 text-slate-400">Loading…</div>
      ) : tutorials.length === 0 ? (
        <div className="text-center py-10 text-slate-400">No tutorials available yet. Your admin can publish them.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tutorials.map(t => (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              className="text-left bg-white border-2 border-slate-200 hover:border-orange-400 rounded-md p-4 transition-colors"
              data-testid={`tutorial-${t.id}`}
            >
              <BookOpen className="w-5 h-5 text-orange-500 mb-2" />
              <h3 className="font-bold text-slate-900 mb-1">{t.title}</h3>
              <p className="text-xs text-slate-500 line-clamp-3">{t.summary}</p>
              <div className="mt-2 text-[10px] text-slate-400">{t.screenshot_urls?.length || 0} screenshot(s)</div>
            </button>
          ))}
        </div>
      )}
      {selected && <TutorialViewerModal tutorial={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export const TutorialViewerModal = ({ tutorial, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="tutorial-viewer">
      <div className="bg-white rounded-md max-w-3xl w-full max-h-[95vh] flex flex-col shadow-2xl">
        <div className="border-b-2 p-4 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold">{tutorial.title}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 prose prose-sm max-w-none">
          {tutorial.summary && <p className="text-slate-600 italic">{tutorial.summary}</p>}
          {/* Render markdown as preformatted text for now (no extra md library) */}
          <div className="whitespace-pre-wrap leading-relaxed text-slate-800">{tutorial.body_markdown}</div>
          {tutorial.screenshot_urls?.length > 0 && (
            <div className="space-y-4">
              <div className="font-bold text-slate-700 uppercase text-xs mt-6">Screenshots</div>
              {tutorial.screenshot_urls.map((url, i) => (
                <figure key={i} className="border-2 border-slate-200 rounded overflow-hidden">
                  <img src={url} alt={tutorial.captions?.[i] || `Step ${i + 1}`} className="w-full" />
                  {tutorial.captions?.[i] && (
                    <figcaption className="bg-slate-100 px-3 py-2 text-sm text-slate-700">{tutorial.captions[i]}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerTutorialsPage;
